import { useState, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import {
  Briefcase, MapPin, Clock, ChevronRight, ArrowLeft,
  Upload, Send, Building2, Users,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeLabels = {
  "full-time": "Full-Time",
  "part-time": "Part-Time",
  volunteer: "Volunteer",
  internship: "Internship",
  contract: "Contract",
};

const locationTypeLabels = {
  "on-site": "On-Site",
  remote: "Remote",
  hybrid: "Hybrid",
};

const CareersPage = () => {
  const { language } = useLanguage();
  const [vacancies, setVacancies] = useState([]);
  const [selectedVacancy, setSelectedVacancy] = useState(null);
  const [showApply, setShowApply] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", cover_letter: "" });
  const [resume, setResume] = useState(null);

  useEffect(() => {
    const fetchVacancies = async () => {
      try {
        const res = await axios.get(`${API}/careers`);
        setVacancies(res.data);
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };
    fetchVacancies();
  }, []);

  const handleApply = async (e) => {
    e.preventDefault();
    if (!selectedVacancy) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("vacancy_id", selectedVacancy.id);
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("phone", form.phone);
      formData.append("cover_letter", form.cover_letter);
      if (resume) formData.append("resume", resume);

      await axios.post(`${API}/careers/apply`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(language === "es" ? "Solicitud enviada con exito" : "Application submitted successfully!");
      setShowApply(false);
      setForm({ name: "", email: "", phone: "", cover_letter: "" });
      setResume(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  const isEs = language === "es";

  // Detail + Apply View
  if (selectedVacancy) {
    return (
      <div className="min-h-screen bg-white">
        <div className="bg-gradient-to-b from-slate-50 to-white py-12">
          <div className="max-w-3xl mx-auto px-4">
            <button
              onClick={() => { setSelectedVacancy(null); setShowApply(false); }}
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-warm-700 mb-6 transition-colors"
              data-testid="careers-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              {isEs ? "Volver a vacantes" : "Back to openings"}
            </button>

            <div className="flex flex-wrap items-start gap-3 mb-4">
              <span className="text-xs px-3 py-1 rounded-full bg-warm-50 text-warm-700 font-medium">
                {typeLabels[selectedVacancy.employment_type] || selectedVacancy.employment_type}
              </span>
              {selectedVacancy.location_type && (
                <span className="text-xs px-3 py-1 rounded-full bg-gray-100 text-gray-600 font-medium">
                  {locationTypeLabels[selectedVacancy.location_type] || selectedVacancy.location_type}
                </span>
              )}
              {selectedVacancy.department && (
                <span className="text-xs px-3 py-1 rounded-full bg-purple-50 text-purple-700 font-medium">
                  {selectedVacancy.department}
                </span>
              )}
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>
              {selectedVacancy.title}
            </h1>

            <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-8">
              {selectedVacancy.location && (
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{selectedVacancy.location}</span>
              )}
              {selectedVacancy.salary_range && (
                <span className="flex items-center gap-1">{selectedVacancy.salary_range}</span>
              )}
              {selectedVacancy.deadline && (
                <span className="flex items-center gap-1"><Clock className="w-4 h-4" />Deadline: {selectedVacancy.deadline}</span>
              )}
            </div>

            {!showApply ? (
              <>
                {selectedVacancy.description && (
                  <div className="mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">{isEs ? "Descripcion" : "Description"}</h2>
                    <div className="text-gray-700 leading-relaxed whitespace-pre-line">{selectedVacancy.description}</div>
                  </div>
                )}
                {selectedVacancy.requirements && (
                  <div className="mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">{isEs ? "Requisitos" : "Requirements"}</h2>
                    <div className="text-gray-700 leading-relaxed whitespace-pre-line">{selectedVacancy.requirements}</div>
                  </div>
                )}
                <Button
                  onClick={() => setShowApply(true)}
                  data-testid="apply-now-btn"
                  className="bg-warm-700 hover:bg-warm-800 text-white rounded-xl px-8 py-3 gap-2"
                >
                  <Send className="w-4 h-4" />
                  {isEs ? "Aplicar ahora" : "Apply Now"}
                </Button>
              </>
            ) : (
              <div className="bg-gray-50 rounded-2xl p-6 mt-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {isEs ? "Enviar solicitud" : "Submit Application"}
                </h2>
                <form onSubmit={handleApply} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-gray-700">{isEs ? "Nombre completo" : "Full Name"} *</Label>
                      <Input
                        data-testid="apply-name"
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-700">Email *</Label>
                      <Input
                        data-testid="apply-email"
                        type="email"
                        value={form.email}
                        onChange={e => setForm({ ...form, email: e.target.value })}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-700">{isEs ? "Telefono" : "Phone"}</Label>
                      <Input
                        data-testid="apply-phone"
                        value={form.phone}
                        onChange={e => setForm({ ...form, phone: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-700">{isEs ? "Curriculum" : "Resume"} (PDF, DOC)</Label>
                      <Input
                        data-testid="apply-resume"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={e => setResume(e.target.files[0] || null)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-700">{isEs ? "Carta de presentacion" : "Cover Letter"}</Label>
                    <textarea
                      data-testid="apply-cover-letter"
                      value={form.cover_letter}
                      onChange={e => setForm({ ...form, cover_letter: e.target.value })}
                      rows={5}
                      placeholder={isEs ? "Cuentanos por que te gustaria unirte..." : "Tell us why you'd like to join..."}
                      className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-warm-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      type="submit"
                      data-testid="apply-submit-btn"
                      disabled={submitting}
                      className="bg-warm-700 hover:bg-warm-800 text-white rounded-xl px-6 gap-2"
                    >
                      <Send className="w-4 h-4" />
                      {submitting ? (isEs ? "Enviando..." : "Submitting...") : (isEs ? "Enviar solicitud" : "Submit Application")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowApply(false)}
                      className="rounded-xl"
                    >
                      {isEs ? "Cancelar" : "Cancel"}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Listing View
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-warm-50 text-warm-700 text-sm font-medium mb-6">
            <Briefcase className="w-4 h-4" />
            {isEs ? "Unete a nuestro equipo" : "Join Our Team"}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>
            {isEs ? "Carreras" : "Careers"}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {isEs
              ? "Haz la diferencia en tu comunidad. Estamos buscando personas apasionadas que quieran ayudar a las familias a sanar y prosperar."
              : "Make a difference in your community. We're looking for passionate people who want to help families heal and thrive."
            }
          </p>
        </div>
      </section>

      {/* Openings */}
      <section className="max-w-3xl mx-auto px-4 pb-20">
        <h2 className="text-xl font-semibold text-gray-900 mb-6" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>
          {isEs ? "Posiciones abiertas" : "Open Positions"} ({vacancies.length})
        </h2>

        {loading ? (
          <div className="py-16 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-warm-700 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : vacancies.length === 0 ? (
          <div className="py-16 text-center bg-gray-50 rounded-2xl" data-testid="no-vacancies">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">
              {isEs ? "No hay vacantes disponibles en este momento." : "No open positions at this time."}
            </p>
            <p className="text-sm text-gray-400">
              {isEs ? "Vuelve a consultar pronto o contactanos." : "Check back soon or contact us."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {vacancies.map(v => (
              <button
                key={v.id}
                onClick={() => setSelectedVacancy(v)}
                data-testid={`vacancy-${v.id}`}
                className="w-full text-left bg-white border border-gray-100 rounded-xl p-5 hover:border-warm-200 hover:shadow-sm transition-all group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 group-hover:text-warm-700 transition-colors">
                      {v.title}
                    </h3>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-500">
                      {v.department && (
                        <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{v.department}</span>
                      )}
                      {v.location && (
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{v.location}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-3.5 h-3.5" />
                        {typeLabels[v.employment_type] || v.employment_type}
                      </span>
                      {v.location_type && (
                        <span>{locationTypeLabels[v.location_type] || v.location_type}</span>
                      )}
                    </div>
                    {v.salary_range && (
                      <p className="text-sm text-warm-700 font-medium mt-2">{v.salary_range}</p>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-warm-600 mt-1 transition-colors" />
                </div>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default CareersPage;
